import type {
  Article,
  RichText,
  SimpleRichText,
  Title,
  List,
  Image,
  AutomatedArticleRichtext,
  EmbeddedHTML,
} from "@/generated/sb";
import { renderRichText } from "@storyblok/react";
import { ISbStoryData } from "storyblok-js-client";

export function calcNumberOfWords(content: RichText) {
  if (!content) {
    return 0;
  }

  const html = renderRichText(content);

  if (!html) {
    return 0;
  }

  const plainText = html.replace(/<[^>]*>/g, " ");
  return plainText
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
}

export function calcReadingTime(content: RichText) {
  const WORDS_PER_MINUTE = 225;
  const numberOfWords = calcNumberOfWords(content);
  const minutes = Math.ceil(numberOfWords / WORDS_PER_MINUTE);

  return minutes;
}

export function generateShareLink(
  social:
    | "linkedin"
    | "x"
    | "facebook"
    | "whatsapp"
    | "messenger"
    | "email"
    | "telegram"
    | "reddit"
    | "pinterest",
  {
    url,
    title,
    description,
  }: {
    url: string;
    title?: string;
    description?: string;
  },
): string {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = title ? encodeURIComponent(title) : "";

  switch (social) {
    case "linkedin": {
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
    }
    case "x": {
      const text = [title, description].filter(Boolean).join(" - ");
      return (
        `https://x.com/intent/tweet?url=${encodedUrl}` +
        (text ? `&text=${encodeURIComponent(text)}` : "")
      );
    }

    case "facebook": {
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    }

    case "whatsapp": {
      const text = [title, url].filter(Boolean).join("\n\n");
      return `https://wa.me/?text=${encodeURIComponent(text)}`;
    }

    case "messenger": {
      return `https://www.facebook.com/dialog/send?link=${encodedUrl}&redirect_uri=${encodedUrl}`;
    }

    case "telegram": {
      const text = title ? `${title}\n\n${url}` : url;
      return `https://t.me/share/url?url=${encodedUrl}&text=${encodeURIComponent(text)}`;
    }

    case "reddit": {
      return `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`;
    }

    case "pinterest": {
      return `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedTitle}`;
    }

    case "email": {
      const subject = title || "Check out this article";
      const body = [description || "", "", `Read more: ${url}`]
        .filter(Boolean)
        .join("\n");

      return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }

    default:
      return "";
  }
}

export function calculateReadTimeFromContent(
  content?: (
    | Image
    | Title
    | SimpleRichText
    | List
    | AutomatedArticleRichtext
    | EmbeddedHTML
  )[],
): string {
  if (!content || content.length === 0) {
    return "1 min";
  }

  let totalWords = 0;

  content.forEach((item) => {
    switch (item.component) {
      case "title":
        if (item.content) {
          // Count words in title (typically shorter)
          totalWords += item.content
            .split(/\s+/)
            .filter((word) => word.length > 0).length;
        }
        break;

      case "simpleRichText":
        if (item.content) {
          totalWords += calcNumberOfWords(item.content);
        }
        break;

      case "list":
        if (item.items) {
          item.items.forEach((listItem) => {
            if (listItem.text) {
              if (typeof listItem.text === "string") {
                totalWords += listItem.text
                  .split(/\s+/)
                  .filter((word) => word.length > 0).length;
              } else {
                // Handle SimpleRichText array case
                listItem.text.forEach((richTextItem) => {
                  if (richTextItem.content) {
                    totalWords += calcNumberOfWords(richTextItem.content);
                  }
                });
              }
            }
          });
        }
        break;

      case "automatedArticleRichtext":
        if (item.richtext) {
          totalWords += calcNumberOfWords(item.richtext);
        }
        break;

      // Images and embeddedHTML don't contribute to read time
      case "image":
      case "embeddedHTML":
        break;
    }
  });

  const WORDS_PER_MINUTE = 225;
  const minutes = Math.max(1, Math.ceil(totalWords / WORDS_PER_MINUTE));

  return `${minutes} min`;
}

export function formatPublicationDate(dateString?: string): string {
  if (!dateString) {
    return "";
  }

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "";
    }

    // Format as "21 Jul 2025"
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const result = document.execCommand("copy");
      textArea.remove();
      return result;
    }
  } catch (err) {
    console.error("Failed to copy text: ", err);
    return false;
  }
}

interface HeroArticleSection {
  component: "heroArticle";
  typography?: Array<{
    typography?: Array<{
      component: string;
      content?: string;
    }>;
  }>;
}

interface ArticleSectionContent {
  component: "articleSection";
  content?: Array<{
    component: string;
    content?: unknown;
  }>;
}

export interface ArticleSchemaData {
  headline: string;
  description?: string;
  datePublished?: string;
  image?: string;
  articleBody?: string;
}

export function extractArticleSchemaData(blok: Article): ArticleSchemaData | null {
  const headline = extractHeadline(blok.sections);
  if (!headline) return null;

  const data: ArticleSchemaData = { headline };

  if (blok.seoDescription) {
    data.description = blok.seoDescription;
  }

  if (blok.publicationDate) {
    const date = new Date(blok.publicationDate.replace(" ", "T"));
    if (!isNaN(date.getTime())) {
      data.datePublished = date.toISOString();
    }
  }

  if (blok.ogImage?.filename) {
    data.image = blok.ogImage.filename;
  }

  const articleBody = extractArticleBody(blok.sections);
  if (articleBody) {
    data.articleBody = articleBody;
  }

  return data;
}

function extractHeadline(sections: Article["sections"]): string | null {
  if (!sections) return null;

  for (const section of sections) {
    if (section.component === "heroArticle") {
      const heroArticle = section as unknown as HeroArticleSection;
      const sectionTitle = heroArticle.typography?.[0];
      const titleItem = sectionTitle?.typography?.[0];
      if (titleItem?.component === "title" && titleItem.content) {
        return titleItem.content;
      }
    }
  }
  return null;
}

function extractArticleBody(sections: Article["sections"]): string | null {
  if (!sections) return null;

  const textParts: string[] = [];

  for (const section of sections) {
    if (section.component === "articleSection") {
      const articleSection = section as unknown as ArticleSectionContent;
      if (!articleSection.content) continue;

      for (const item of articleSection.content) {
        if (item.component === "simpleRichText" && item.content) {
          const html = renderRichText(item.content as Parameters<typeof renderRichText>[0]);
          if (html) {
            let stripped = String(html);
            let prev: string;
            do {
              prev = stripped;
              stripped = stripped.replace(/<[^>]*>/g, "");
            } while (stripped !== prev);
            const text = stripped.trim();
            if (text) {
              textParts.push(text);
            }
          }
        }
      }
    }
  }

  if (textParts.length === 0) return null;
  return textParts.join(" ").slice(0, 1000);
}
